import { useEffect, useState } from 'react';

/**
 * Visible offline-state indicator (PWA-02).
 *
 * Tracks `navigator.onLine` plus the `online`/`offline` window events and
 * renders a small fixed banner while offline, so users viewing a previously
 * cached shell (served by the service worker's navigateFallback) know they
 * are looking at stale/last-loaded data rather than a broken app.
 *
 * Renders nothing while online.
 */
export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-50 bg-amber-100 pt-[calc(0.5rem+var(--safe-area-inset-top,env(safe-area-inset-top,0px)))] pb-2 pl-[calc(1rem+var(--sa-left))] pr-[calc(1rem+var(--sa-right))] text-center text-sm font-medium text-amber-900 shadow-sm"
    >
      You&apos;re offline — showing your last loaded view.
    </div>
  );
}
